import { Layout } from '../components/layout/Layout';
import { ContentSection, SectionHeader } from '../components/sections/ContentSection';
import { ContactForm } from '../components/forms/ContactForm';
import { usePublicData } from '../hooks/usePublicData';
import { SEO } from '../components/SEO';
import { MapPin, Phone, Mail, Clock, Loader2 } from 'lucide-react';

// Default contact settings
const defaultContact = {
  address: ["89 South Broadway", "Geneva, Ohio 44041"],
  phone: "(440) 466-2817",
  email: "genevaumchurch@windstream.net",
  officeHours: [
    { days: "Monday - Friday", hours: "9:00 AM - 3:00 PM" },
    { days: "Sunday", hours: "Before & After Services" }
  ],
  pastor: {
    name: "Pastor Pam Buzalka",
    title: "Senior Pastor",
    description: "Pastor Pam is available for appointments and pastoral visits. Please contact the church office to schedule a meeting."
  },
  mapEmbedUrl: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2983.8234567890123!2d-80.94727317!3d41.803778!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88318dd43e449bab%3A0xe90deb16b6a4c8b7!2s89%20S%20Broadway%2C%20Geneva%2C%20OH%2044041!5e0!3m2!1sen!2sus!4v1234567890123",
  accessibilityNote: "Handicapped access and elevator available from rear entrance. If you need assistance, please call ahead and we'll be happy to help."
};

export default function Contact() {
  const { data: contactData, loading } = usePublicData('contact');
  
  // Use fetched data or defaults
  const contact = contactData && Object.keys(contactData).length > 0 ? contactData : defaultContact;

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-secondary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEO 
        title="Contact Us"
        description="Contact Geneva United Methodist Church. Located at 89 South Broadway, Geneva, Ohio. Call (440) 466-2817 or send us a message."
        url="/contact"
      />
      
      {/* Hero Banner */}
      <section className="relative h-[40vh] min-h-[300px] flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-primary-900" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4" data-testid="contact-page-title">
            Contact Us
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            We'd love to hear from you. Reach out with any questions.
          </p>
        </div>
      </section>

      {/* Contact Info & Form */}
      <ContentSection bgColor="bg-white">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Contact Information */}
          <div>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-primary-900 mb-8">
              Get in Touch
            </h2>
            
            <div className="space-y-6 mb-10">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-secondary" />
                </div>
                <div>
                  <h3 className="font-semibold text-primary-900 mb-1">Address</h3>
                  {contact.address.map((line) => (
                    <p key={`addr-${line}`} className="text-slate-600">{line}</p>
                  ))}
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-secondary" />
                </div>
                <div>
                  <h3 className="font-semibold text-primary-900 mb-1">Phone</h3>
                  <a href={`tel:${contact.phone.replace(/[^0-9]/g, '')}`} className="text-slate-600 hover:text-secondary transition-colors">
                    {contact.phone}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-secondary" />
                </div>
                <div>
                  <h3 className="font-semibold text-primary-900 mb-1">Email</h3>
                  <a href={`mailto:${contact.email}`} className="text-slate-600 hover:text-secondary transition-colors break-all">
                    {contact.email}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-secondary" />
                </div>
                <div>
                  <h3 className="font-semibold text-primary-900 mb-1">Office Hours</h3>
                  {contact.officeHours.map((hours) => (
                    <p key={`hours-${hours.days}`} className="text-slate-600">{hours.days}: {hours.hours}</p>
                  ))}
                </div>
              </div>
            </div>

            {/* Pastor Info */}
            <div className="bg-slate-50 rounded-xl p-6 border-l-4 border-secondary">
              <h3 className="font-heading text-lg font-semibold text-primary-900 mb-2">
                {contact.pastor.name}
              </h3>
              {contact.pastor.title && (
                <p className="text-sm text-secondary font-medium mb-2">{contact.pastor.title}</p>
              )}
              <p className="text-slate-600 text-sm">
                {contact.pastor.description}
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-primary-900 mb-8">
              Send Us a Message
            </h2>
            <ContactForm />
          </div>
        </div>
      </ContentSection>

      {/* Map */}
      <ContentSection bgColor="bg-slate-50" paddingY="py-12">
        <SectionHeader 
          title="Find Us"
          subtitle="We're located in the heart of Geneva, Ohio"
        />
        <div className="rounded-2xl overflow-hidden shadow-lg h-[400px]">
          <iframe
            src={contact.mapEmbedUrl}
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Church Location"
            data-testid="contact-map"
          />
        </div>
      </ContentSection>

      {/* Additional Info */}
      {contact.accessibilityNote && (
        <ContentSection bgColor="bg-white" paddingY="py-12">
          <div className="max-w-2xl mx-auto text-center">
            <p className="text-slate-600">
              <strong className="text-primary-900">Accessibility:</strong> {contact.accessibilityNote}
            </p>
          </div>
        </ContentSection>
      )}
    </Layout>
  );
}
